import { AccountAttachmentFormContext, AccountAttachmentRequirementStatus } from "models/Account";
import React, { useState } from "react";
import {
    Alert,
    Button,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from "reactstrap";
import { getAccountAttachmentForm, submitAccountAttachmentForm } from "services/AccountApi";
import SignaturePad from "./Projects/SignaturePad";
import { useAccountTranslation } from "./translations";

interface Props {
    accountId: string;
    requirement: AccountAttachmentRequirementStatus;
    onSubmitted: () => Promise<void>;
}

const AccountAttachmentFormWizard: React.FC<Props> = ({ accountId, requirement, onSubmitted }) => {
    const { locale, t } = useAccountTranslation();
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);
    const [context, setContext] = useState<AccountAttachmentFormContext | null>(null);
    const [declarations, setDeclarations] = useState<boolean[]>([]);
    const [validityConsent, setValidityConsent] = useState(false);
    const [truthConsent, setTruthConsent] = useState(false);
    const [signature, setSignature] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const start = async () => {
        setOpen(true);
        setStep(0);
        setContext(null);
        setDeclarations([]);
        setValidityConsent(false);
        setTruthConsent(false);
        setSignature(null);
        setError("");
        setLoading(true);
        try {
            const value = await getAccountAttachmentForm(accountId, requirement.requirement.id);
            setContext(value);
            setDeclarations((value.requirement.declarations ?? []).map(() => false));
        } catch {
            setError(t("attachmentFormLoadError"));
        } finally {
            setLoading(false);
        }
    };

    const submit = async () => {
        if (!signature || !validityConsent || !truthConsent) return;
        setSaving(true);
        setError("");
        try {
            await submitAccountAttachmentForm(accountId, requirement.requirement.id, {
                declaration_values: declarations,
                validity_consent: validityConsent,
                truth_consent: truthConsent,
                signature_data: signature,
            });
            await onSubmitted();
            setOpen(false);
        } catch {
            setError(t("attachmentFormSubmitError"));
        } finally {
            setSaving(false);
        }
    };

    const address = context?.company.address;
    const addressLines = address ? [
        [address.street, address.number, address.addition].filter(Boolean).join(" "),
        [address.postal_code, address.city].filter(Boolean).join(" "),
        [address.region, address.country].filter(Boolean).join(", "),
    ].filter(Boolean) : [];
    const dates = context ? {
        from: new Date(context.valid_from).toLocaleDateString(locale),
        until: new Date(context.valid_until).toLocaleDateString(locale),
    } : null;

    return <>
        <Button color="primary" onClick={start}>
            <i className="mdi mdi-form-select me-1" aria-hidden="true" />
            {t("attachmentFormStart")}
        </Button>
        <Modal isOpen={open} toggle={() => !saving && setOpen(false)} size="lg" backdrop="static">
            <ModalHeader toggle={() => !saving && setOpen(false)}>
                {requirement.requirement.name}
            </ModalHeader>
            <ModalBody>
                <div className="d-flex flex-wrap gap-2 mb-4" aria-label={t("attachmentFormProgress")}>
                    {["attachmentFormStepCompany", "attachmentFormStepDeclarations", "attachmentFormStepValidity", "attachmentFormStepSign"]
                        .map((key, index) => <span
                            key={key}
                            className={`badge ${index === step ? "bg-primary" : index < step ? "bg-success" : "bg-light text-dark"}`}
                        >{index + 1}. {t(key)}</span>)}
                </div>
                {error && <Alert color="danger">{error}</Alert>}
                {loading && <div className="py-5 text-center"><Spinner /></div>}
                {!loading && context && step === 0 && <div>
                    <Alert color="info">{t("attachmentFormCompanyReadOnly")}</Alert>
                    <dl className="row mb-0">
                        <dt className="col-sm-4">{t("attachmentFormKvkNumber")}</dt>
                        <dd className="col-sm-8">{context.company.kvk_number}</dd>
                        <dt className="col-sm-4">{t("attachmentFormRegisteredName")}</dt>
                        <dd className="col-sm-8">{context.company.registered_name}</dd>
                        {(context.company.trade_names ?? []).length > 0 && <>
                            <dt className="col-sm-4">{t("attachmentFormTradeNames")}</dt>
                            <dd className="col-sm-8">{context.company.trade_names?.join(", ")}</dd>
                        </>}
                        <dt className="col-sm-4">{t("attachmentFormRegisteredAddress")}</dt>
                        <dd className="col-sm-8">{addressLines.map(line => <div key={line}>{line}</div>)}</dd>
                        <dt className="col-sm-4">{t("attachmentFormRepresentative")}</dt>
                        <dd className="col-sm-8">{context.company.representative}</dd>
                    </dl>
                </div>}
                {!loading && context && step === 1 && <div>
                    <p>{t("attachmentFormDeclarationsIntro")}</p>
                    {(context.requirement.declarations ?? []).map((declaration, index) => <FormGroup
                        check
                        className={`border rounded p-3 ps-5 mb-2 ${declarations[index] ? "border-success bg-light" : "border-warning"}`}
                        key={`${index}-${declaration}`}
                    >
                        <Input
                            id={`attachment-declaration-${index}`}
                            type="checkbox"
                            checked={declarations[index] ?? false}
                            onChange={event => setDeclarations(values => values.map((value, current) =>
                                current === index ? event.target.checked : value
                            ))}
                        />
                        <Label for={`attachment-declaration-${index}`} check>{declaration}</Label>
                        {!declarations[index] && <div className="small text-warning mt-1">
                            {t("attachmentFormUncheckedPdfWarning")}
                        </div>}
                    </FormGroup>)}
                </div>}
                {!loading && context && step === 2 && dates && <div>
                    <Alert color="light">
                        {t("attachmentFormValidityPeriod", { from: dates.from, until: dates.until })}
                    </Alert>
                    <FormGroup check>
                        <Input
                            id="attachment-validity-consent"
                            type="checkbox"
                            checked={validityConsent}
                            onChange={event => setValidityConsent(event.target.checked)}
                        />
                        <Label for="attachment-validity-consent" check>
                            {t("attachmentFormValidityConsent", { until: dates.until })}
                        </Label>
                    </FormGroup>
                </div>}
                {!loading && context && step === 3 && <div>
                    <FormGroup check className="mb-3">
                        <Input
                            id="attachment-truth-consent"
                            type="checkbox"
                            checked={truthConsent}
                            onChange={event => setTruthConsent(event.target.checked)}
                        />
                        <Label for="attachment-truth-consent" check>{t("attachmentFormTruthConsent")}</Label>
                    </FormGroup>
                    <h3 className="h5">{t("attachmentFormSignature")}</h3>
                    <p>{t("attachmentFormSignatureIntro")}</p>
                    <SignaturePad onChange={setSignature} />
                </div>}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={() => step > 0 ? setStep(value => value - 1) : setOpen(false)} disabled={saving}>
                    {step > 0 ? t("attachmentFormPrevious") : t("cancel")}
                </Button>
                {context && step < 3 && <Button
                    color="primary"
                    onClick={() => setStep(value => value + 1)}
                    disabled={step === 2 && !validityConsent}
                >{t("attachmentFormNext")}</Button>}
                {context && step === 3 && <Button
                    color="primary"
                    onClick={submit}
                    disabled={saving || !truthConsent || !signature}
                >
                    {saving ? <><Spinner size="sm" className="me-2" />{t("attachmentFormCreating")}</> : t("attachmentFormSignAndSubmit")}
                </Button>}
            </ModalFooter>
        </Modal>
    </>;
};

export default AccountAttachmentFormWizard;
